<script setup lang="ts">
// Vue & Plugins
import { capitalize, computed } from 'vue'
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router'
import { useHasPermission } from '../../../permissions/composables/useHasPermission'
import { useResourceNames } from '../../composables/resource-names'
import { useFeatureFlags } from '../../../features/composables/useFeatureFlag'

// Data
const route = useRoute()
const router = useRouter()

const { can, canManageShifts } = useHasPermission()
const { isFeatureEnabled, isEnabledForRegion } = useFeatureFlags()

const navItems = computed(() => {
  return (route.meta?.childNavItems ?? [])
    .map((item) => item)
    // Filter out items that the user doesn't have permission to access
    .filter((item) => {
      if (item.route === '/(app)/schedule/calendar/') {
        return isEnabledForRegion.value
      }

      if (item.route === '/(app)/schedule/shifts.wizard') {
        return canManageShifts.value
      }

      if (item.permission) {
        return can(item.permission.ability, item.permission.subject)
      }

      return true
    })
    // Filter out items based on feature flags
    .filter((item) => {
      if (item.feature) {
        return isFeatureEnabled(item.feature).value
      }

      return true
    })
})
const selected = (event: Event) => {
  const route = (event.target as HTMLSelectElement).value
  router.push({ name: route } as RouteLocationRaw)
}

const { getTerm } = useResourceNames()
</script>

<template>
  <!-- TODO: THEME -->
  <header class="shadow-sm bg-white dark:bg-transparent">
    <div class="mx-auto px-4 pt-4 sm:pt-0 border-b-2 border-surface">
      <div class="pb-5 sm:pb-0">
        <div class="">
          <div class="sm:hidden">
            <label
              for="current-tab"
              class="sr-only"
            >Select a tab</label>
            <select
              id="current-tab"
              name="current-tab"
              class="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm capitalize"
              @change="selected"
            >
              <option
                v-for="item in navItems"
                :key="item.label"
                :selected="route.name === item.route"
                :value="item.route"
              >
                {{ getTerm(item.label) }}
              </option>
            </select>
          </div>
          <div class="hidden sm:block">
            <nav class="flex space-x-8 overflow-x-auto">
              <!-- TODO: RELEASE - Is this safe and performant? We need to use singular or plural for route labels and remember to pluralise or singularise them. This will be difficult to maintain if we want to just display labels from route files. -->
              <Button
                v-for="item in navItems"
                :key="item.label"
                :label="capitalize(getTerm(item.label).value)"
                class="!py-3 border-b-4 border-0"
                :class="{
                  'text-primary-emphasis border-primary-emphasis': route.matched.some(({ name }) => name === item.route),
                  'text-muted-color hover:text-primary-emphasis': !route.matched.some(({ name }) => name === item.route),
                }"
                :icon="item.icon"
                text
                @click="router.push({ name: item.route } as RouteLocationRaw)"
              />
            </nav>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>
